<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue'
import CustomInput from '@/components/Form/CustomInput.vue'
import { useGlobalStore } from '@/store'
import CustomSelect from "@/components/Form/CustomSelect.vue";

const isValidForm = ref(false)
const isInitial = ref(true)
const searchSubmitted = ref(false)
const searchVisible = ref(true)
const searchedValue = ref('')
const searchHitVisible = ref(true)
const searchHitActionsVisible = ref(true)
const globalStore = useGlobalStore()
const product = computed(() => globalStore.getProductById(props.id))

const props = defineProps({
  index: {
    type: Number,
    default: 1
  },
  id: {
    type: String,
    default: ''
  },
  dealers: {
    type: Array,
    default: () => []
  }
})
const emits = defineEmits(['delete'])

function deleteProductInfo () {
  if (props.index > 1) {
    emits('delete')
  }
}

async function searchProductWithSerialNr ()
{
  searchSubmitted.value = true
  searchedValue.value = product.value.serialNumber

  if (product.value.serialNumber) {
    const itemNo = await globalStore.validateStepOneProductSerialNr(product.value.serialNumber)
    isInitial.value = false

    if (itemNo) {
      await globalStore.fetchStepOneProductIfValidSerialNr(itemNo, props.id)
      searchHitVisible.value = true
      product.value.valid = true
      globalStore.stepOne.hideSerialNumberErrorAlert = true
    } else {
      product.value.valid = false
      searchHitVisible.value = false
      globalStore.stepOne.hideSerialNumberErrorAlert = false
    }
  }
  else {
    product.value.valid = false
    searchHitVisible.value = false
  }
}
function resetProductSerialNumber () {
  product.value.valid = false
  searchSubmitted.value = false
  product.value.searchedProductHit = {}
  searchHitVisible.value = true
  searchVisible.value = true
  searchHitActionsVisible.value = true
  product.value.serialNumber = ''
}

function handleSearchHitSelect () {
  searchVisible.value = false
  searchHitVisible.value = false
  searchHitActionsVisible.value = false
}

function openSearchHitBox () {
  searchHitVisible.value = true
}

function handleMouseLeave () {
  if (!searchHitActionsVisible.value)
  {
    searchHitVisible.value = false
  }
}

function validateForm () {
  return !!(product.value.serialNumber !== '' && product.value.valid && product.value.buyDate !== '' && product.value.buyPrice !== '' && product.value.invoiceNr && product.value.dealer !== '')
}

watch(product, () => {
  if (searchedValue.value !== product.value.serialNumber) {
    searchedValue.value = product.value.serialNumber
    product.value.valid = false
    product.value.searchedProductHit = {}
    searchSubmitted.value = false
  }

  isValidForm.value = validateForm()
}, { deep: true })
</script>

<template>
  <div v-if="product" @mouseleave="handleMouseLeave" class="step-one-products-product" :class="{'-valid-form' : isValidForm}">
    <div class="step-one--product-header">
      <div class="step-one--product-header-title">
        <span class="bold">{{ $t('stepOne.productInfoTitle') }} {{props.index}}</span>
      </div>
      <div v-if="props.index > 1 && !(product.valid && searchSubmitted)" @click="deleteProductInfo" class="step-one--product-header-delete">
        <span class="icon-close-before"></span>
      </div>
    </div>
    <div class="step-one--product-top" :class="{'-serial-number-valid': product.valid && searchSubmitted}">
      <div class="step-one--product-top-serial-nr" :class="{'-serial-number-initial-not-valid': !product.valid && isInitial,'-serial-number-not-valid': !product.valid && searchSubmitted && !isInitial, '-serial-number-valid': product.valid && searchSubmitted}">
        <CustomInput v-model="product.serialNumber" :required="true" :placeholder-label="$t('stepOne.productInfo.placeholders.serialNr')" :id="'reward-campaign-product-' + props.index + '-serial-nr-input'" />
      </div>

      <div class="step-one--product-top-search-actions">
        <div v-if="searchVisible" class="-btn -btn-alpha" @click="searchProductWithSerialNr">
          <span>{{ $t('stepOne.productInfoSearch') }}</span>
        </div>
        <div v-else class="step-one--product-top-search-actions-valid">
          <div @click="openSearchHitBox" @mouseover="openSearchHitBox" class="step-one--product-top-search-indicator-valid">
            <span class="icon-check-before"></span>
          </div>
          <div @click="resetProductSerialNumber" class="step-one--product-top-search-actions-delete">
            <span class="icon-close-before"></span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="product.searchedProductHit.imageUrl  && searchHitVisible" class="step-one--product-search-hit">
      <div class="product-search-hit--content">
        <div class="product-search-hit--image-wrapper">
          <img :src="product.searchedProductHit.imageUrl" :alt="product.searchedProductHit.tpgName" loading="lazy"/>
        </div>
        <div class="product-search-hit--info-wrapper">
          <span class="heading delta">{{product.searchedProductHit.tpgCode}}</span>
          <span class="heading gamma -color-alpha">{{product.searchedProductHit.tpgName}}</span>
          <span>{{ $t('stepOne.productInfoArticleNo') }} {{product.searchedProductHit.itemNo}}</span>
        </div>
      </div>
      <div class="product-search-hit--actions-wrapper">
        <div v-if="searchHitActionsVisible" class="product-search-hit--actions">
          <div @click="resetProductSerialNumber" class="-btn -btn-eta">
            {{ $t('stepOne.productInfo.reset') }}
          </div>
          <div @click="handleSearchHitSelect" class="-btn -btn-alpha">
            {{ $t('stepOne.productInfo.select') }}
          </div>
        </div>
      </div>
    </div>
    <div class="step-one--product-mid">
      <div class="step-one--product-mid-buy-date">
        <CustomInput v-model="product.buyDate" :required="true" :type="'date'" :placeholder-label="$t('stepOne.productInfo.placeholders.buyDate')" :id="'reward-campaign-product-' + props.index + '-buy-date'" />
      </div>
      <div class="step-one--product-mid-buy-price">
        <CustomInput v-model="product.buyPrice" :required="true" :placeholder-label="$t('stepOne.productInfo.placeholders.buyPrice')" :id="'reward-campaign-product-' + props.index + '-buy-price'" />
      </div>
    </div>
    <div class="step-one--product-bot">
      <div class="step-one--product-bot-inv-nr">
        <CustomInput v-model="product.invoiceNr" :required="true" :placeholder-label="$t('stepOne.productInfo.placeholders.invoiceNr')" :id="'reward-campaign-product-' + props.index + '-invoice-nr'" />
      </div>
      <div class="step-one--product-bot-dealer">
        <CustomInput v-if="props.dealers.length < 1" v-model="product.dealer" :required="true" :placeholder-label="$t('stepOne.productInfo.placeholders.dealer')" :id="'reward-campaign-product-' + props.index + '-dealer'" />
        <CustomSelect v-else v-model="product.dealer" :options="dealers" :required="true" :placeholder-label="$t('stepOne.productInfo.placeholders.dealerSelect')" :id="'reward-campaign-product-' + props.index + '-dealer'" />
      </div>
    </div>
  </div>
</template>
